import { useEffect, useState } from "react";
import axios from "axios";
import { alpha } from "@mui/material/styles";
import { API_URL } from "../../../constants";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Divider,
  Chip,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  useTheme,
} from "@mui/material";
import { ShoppingCart } from "lucide-react";
import CardMarkerSmall from "../../../components/CockpitCards/CardMarkerSmall";
import AddOnSubText from "./AddOnSubText";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "../../../language";


export default function AccountAddOns() {

  const theme = useTheme();
  const { t } = useTranslation();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    axios
      .get(`${API_URL}/api/account/add-ons/`)
      .then((res) => setData(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const toggleExpand = (key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const downloadInvoice = async (order) => {
    const invoiceId = order.metadata?.invoice_id || order.stripe_invoice_id;
    if (!invoiceId) return;

    const res = await axios.get(
      `${API_URL}/api/billing/invoice/${invoiceId}/download/`,
      { responseType: "blob" }
    );

    const blob = new Blob([res.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    window.open(url);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Typography color="error">{t("addon_fetch_error")}</Typography>;
  }

  if (!data?.lab_visits?.length) {
    return <Typography color="text.secondary">{t("addon_no_purchases")}</Typography>;
  }

  const isDark = theme.palette.mode === 'dark';
  const glassCardSx = {
    bgcolor: isDark ? alpha('#fff', 0.04) : alpha('#fff', 0.5),
    backdropFilter: 'blur(28px) saturate(180%)',
    WebkitBackdropFilter: 'blur(28px) saturate(180%)',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.65)'}`,
    boxShadow: isDark
      ? 'inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)'
      : 'inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)',
  };
  const cardStyle = {
    ...glassCardSx,
    borderRadius: 4,
    overflow: 'hidden',
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', width: '100%', px: { xs: 2, sm: 0 } }}>
      <CardMarkerSmall
        sx={glassCardSx}
        headline={t("addon_purchases_headline")}
        subtext={<AddOnSubText />}
        cardlines={
          <Paper elevation={0} sx={{ ...cardStyle, mt: 2 }}>
            {data.lab_visits.map((visit, idx) => {
              const accentColor = idx % 2 === 0
                ? theme.palette.tertiary.C300
                : theme.palette.quartiary.C300;
              const isExpanded = !!expanded[visit.lab_visit_date];

              return (
                <Box key={visit.lab_visit_date}>
                  {idx > 0 && <Divider />}

                  {/* Row header */}
                  <Box
                    sx={{
                      px: 2, py: 1.5,
                      borderLeft: `10px solid ${accentColor}`,
                      cursor: 'pointer',
                      transition: 'background-color 0.15s',
                      '&:hover': {
                        bgcolor: theme.palette.mode === 'light' ? 'grey.C100' : 'greydark.C900',
                      },
                    }}
                    onClick={() => toggleExpand(visit.lab_visit_date)}
                  >
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <ShoppingCart size={20} color={accentColor} style={{ flexShrink: 0 }} />

                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {t("addon_selected_for")} {visit.lab_visit_date}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {visit.orders.length} {t("addon_orders_label")}
                        </Typography>
                      </Box>

                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); toggleExpand(visit.lab_visit_date); }}
                        sx={{ flexShrink: 0 }}
                      >
                        <ExpandMoreIcon sx={{
                          rotate: isExpanded ? '180deg' : '0deg',
                          transition: 'rotate 0.2s',
                        }} />
                      </IconButton>
                    </Stack>
                  </Box>

                  {/* Expanded order details */}
                  <Collapse in={isExpanded}>
                    <Box sx={{
                      px: 2, pt: 1, pb: 2,
                      borderLeft: `10px solid ${accentColor}`,
                      bgcolor: isDark ? alpha('#fff', 0.03) : alpha('#fff', 0.35),
                    }}>
                      <Stack spacing={1.5}>
                        {visit.orders.map((order) => (
                          <Card
                            key={order.id}
                            variant="outlined"
                            sx={{
                              ...glassCardSx,
                              borderRadius: 2,
                            }}
                          >
                            <CardContent sx={{ pb: '12px !important' }}>
                              <Stack spacing={1.5}>

                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                  <Typography variant="body2" fontWeight={600}>
                                    {t("addon_orders_label")}
                                  </Typography>
                                  <Chip
                                    label={order.paid ? t("addon_status_paid") : t("addon_status_unpaid")}
                                    color={order.paid ? "success" : "warning"}
                                    size="small"
                                  />
                                </Stack>

                                <Divider />

                                <Stack spacing={0.75}>
                                  {order.items.map((item) => (
                                    <Stack
                                      key={item.id}
                                      direction="row"
                                      justifyContent="space-between"
                                      alignItems="center"
                                    >
                                      <Typography variant="body2">
                                        {item.product_name.name} ×{item.quantity}
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary">
                                        € {(item.unit_price_cents / 100).toFixed(2)}
                                      </Typography>
                                    </Stack>
                                  ))}
                                </Stack>

                                <Divider />

                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                  <Typography variant="body2" fontWeight={600}>
                                    {t("addon_total_vat")}
                                  </Typography>
                                  <Stack direction="row" alignItems="center" spacing={2}>
                                    <Typography variant="body2" fontWeight={600}>
                                      € {(order.total_amount / 100).toFixed(2)}
                                    </Typography>
                                    {order.stripe_invoice_id && (
                                      <Button
                                        size="small"
                                        startIcon={<ReceiptLongIcon />}
                                        onClick={() => downloadInvoice(order)}
                                      >
                                        {t("addon_invoice")}
                                      </Button>
                                    )}
                                  </Stack>
                                </Stack>

                              </Stack>
                            </CardContent>
                          </Card>
                        ))}
                      </Stack>
                    </Box>
                  </Collapse>

                </Box>
              );
            })}
          </Paper>
        }
      />
    </Box>
  );
}
